import { useCallback, useEffect, useState } from "react";
import { MdRefresh, MdShowChart } from "react-icons/md";
import AdminMasterAccountPanel from "components/admin/AdminMasterAccountPanel";
import Card from "components/card";
import { adminFetch } from "utils/adminFetch";

export default function AdminMasterResults() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authRequired, setAuthRequired] = useState(false);
  const [masters, setMasters] = useState([]);
  const [activeMasterId, setActiveMasterId] = useState("");

  const loadMasters = useCallback(async (refresh = false) => {
    setLoading(true);
    setError("");
    setAuthRequired(false);
    try {
      const qs = refresh ? "?refresh=1" : "";
      const res = await adminFetch(`/api/admin/master-results${qs}`);
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const needsAuth =
          res.status === 401 || data.code === "ADMIN_AUTH_REQUIRED";
        setAuthRequired(needsAuth);
        setError(
          needsAuth
            ? "Session admin expirée ou absente."
            : data.message || "Could not load master accounts.",
        );
        setMasters([]);
        return;
      }
      const rows = Array.isArray(data.masters) ? data.masters : [];
      setMasters(rows);
      setActiveMasterId((prev) => {
        if (prev && rows.some((m) => m.id === prev)) return prev;
        return rows[0]?.id || "";
      });
    } catch {
      setError("Network error.");
      setMasters([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMasters(false);
  }, [loadMasters]);

  const activeMaster =
    masters.find((m) => m.id === activeMasterId) || masters[0] || null;

  return (
    <div className="mt-3 flex flex-col gap-5">
      <Card extra="w-full p-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <MdShowChart className="h-6 w-6 text-brand-500" />
              <h1 className="text-xl font-bold text-navy-700 dark:text-white">
                Master results
              </h1>
            </div>
            <p className="mt-2 max-w-3xl text-sm text-gray-600 dark:text-gray-400">
              Gère chaque compte master séparément : choisis les paires à afficher
              et la période, puis publie sur la page publique <strong>/Results</strong>.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadMasters(true)}
            disabled={loading}
            className="inline-flex shrink-0 items-center gap-1 rounded-xl border border-gray-200 px-3 py-2 text-xs font-semibold text-navy-700 hover:bg-gray-50 disabled:opacity-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
          >
            <MdRefresh className="h-4 w-4" />
            {loading ? "Chargement…" : "Tout actualiser"}
          </button>
        </div>

        {error ? (
          <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
            <p>{error}</p>
            {authRequired ? (
              <a
                href="/admin/login"
                className="mt-2 inline-block font-semibold text-red-800 underline dark:text-red-200"
              >
                → Se connecter en admin
              </a>
            ) : null}
          </div>
        ) : null}

        {loading && !masters.length && !error ? (
          <p className="mt-4 text-sm text-gray-500">Chargement des comptes master…</p>
        ) : !masters.length && !error ? (
          <p className="mt-4 text-sm text-gray-500">
            Aucun compte master configuré. Édite{" "}
            <code className="text-xs">backend/data/master-results-accounts.json</code>.
          </p>
        ) : !masters.length && error ? null : (
          <div className="mt-4 flex flex-wrap gap-2">
            {masters.map((master) => {
              const active = master.id === activeMasterId;
              const label =
                master.name ||
                master.accountNumber ||
                master.id;
              return (
                <button
                  key={master.id}
                  type="button"
                  onClick={() => setActiveMasterId(master.id)}
                  className={`rounded-xl px-4 py-2 text-sm font-semibold transition ${
                    active
                      ? "bg-brand-500 text-white"
                      : "border border-gray-200 text-navy-700 hover:bg-gray-50 dark:border-white/10 dark:text-white dark:hover:bg-white/5"
                  }`}
                >
                  {label}
                  {master.liveUpdateEnabled ? (
                    <span
                      className="ml-2 inline-block h-2 w-2 rounded-full bg-emerald-400"
                      title="LiveUpdate On"
                    />
                  ) : null}
                  {master.totalTradeCount != null ? (
                    <span className="ml-2 text-xs opacity-80">
                      ({master.totalTradeCount})
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </Card>

      {activeMaster ? (
        <AdminMasterAccountPanel
          key={activeMaster.id}
          masterSummary={activeMaster}
          onRefreshAll={() => void loadMasters(false)}
        />
      ) : null}
    </div>
  );
}
